import type { FC } from 'react';
import { useState, useEffect, useRef } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';

const services = ['Branding', 'Marketing', 'Websites', 'LLM SEO', 'Content', 'Paid Ads'];

export const HomeHero: FC = () => {
  const [wordIndex, setWordIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % services.length);
        setVisible(true);
      }, 280);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  const aboutRef = useRef<HTMLDivElement>(null);
  const [aboutVisible, setAboutVisible] = useState(false);

  useEffect(() => {
    const node = aboutRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // threshold 0: fire as soon as ANY pixel enters the viewport.
        // A non-zero threshold can never be met by an element taller
        // than the viewport, which made tall mobile sections stay
        // invisible. Once shown, disconnect so it never hides again.
        if (entry.isIntersecting) {
          setAboutVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      sx={{
        background: '#ffffff',
        position: 'relative',
        pt: { xs: '110px', md: '140px' },
        pb: { xs: '60px', md: '80px' }
      }}
    >
      <Container maxWidth="lg">
        {/* Location tag */}
        <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 4, md: 5 } }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{
              pl: 0.75,
              pr: 2,
              py: 0.5,
              borderRadius: 999,
              background: '#ffffff',
              border: '1px solid #e4e4e7',
              boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#2563eb',
                transform: 'translateY(-1px)'
              }
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                fontSize: 12,
                fontWeight: 700
              }}
            >
              ✶
            </Box>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: 13,
                fontWeight: 500,
                color: '#0a0a0b',
                letterSpacing: '-0.005em'
              }}
            >
              Based in <Box component="span" sx={{ fontWeight: 700 }}>Katy, TX</Box>
            </Typography>
          </Stack>
        </Stack>

        {/* Headline */}
        <Stack spacing={{ xs: 3, md: 4 }} alignItems="center" sx={{ mb: { xs: 5, md: 7 } }}>
          <Typography
            component="h1"
            align="center"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: { xs: '44px', sm: '64px', md: '88px' },
              fontWeight: 700,
              lineHeight: 1.02,
              letterSpacing: '-0.04em',
              color: '#0a0a0b',
              maxWidth: 1000
            }}
          >
            We handle it all,
          </Typography>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: 60, sm: 84, md: 108 }
            }}
          >
            <Box
              component="span"
              sx={{
                position: 'relative',
                display: 'inline-block',
                px: { xs: 1, md: 1.5 },
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '44px', sm: '64px', md: '88px' },
                fontWeight: 700,
                fontStyle: 'italic',
                lineHeight: 1.02,
                letterSpacing: '-0.04em',
                color: '#0a0a0b',
                whiteSpace: 'nowrap',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(8px)',
                transition: 'all 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: { xs: '-8%', md: '-10%' },
                  height: { xs: '24%', md: '26%' },
                  background: 'linear-gradient(180deg, transparent 0%, rgba(147, 197, 253, 0.55) 0%, rgba(96, 165, 250, 0.45) 100%)',
                  borderRadius: 1,
                  zIndex: -1,
                  transformOrigin: 'left center',
                  animation: visible ? 'highlightSweep 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none'
                },
                '@keyframes highlightSweep': {
                  '0%': { transform: 'scaleX(0)' },
                  '100%': { transform: 'scaleX(1)' }
                }
              }}
            >
              {services[wordIndex]}
            </Box>
          </Box>
          <Typography
            align="center"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: { xs: 16, md: 19 },
              fontWeight: 400,
              lineHeight: 1.55,
              letterSpacing: '-0.01em',
              color: '#52525b',
              maxWidth: 580
            }}
          >
            One team. Every channel.{' '}
            <Box component="span" sx={{ color: '#0a0a0b', fontWeight: 600 }}>
              We help ambitious brands grow.
            </Box>
          </Typography>
        </Stack>

        {/* Big media card */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: 4,
            overflow: 'hidden',
            aspectRatio: '16 / 9',
            background: '#0a0a0b',
            boxShadow: '0 30px 80px -20px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.05)'
          }}
        >
          <Box
            component="iframe"
            src="https://www.youtube.com/embed/D5Yy2XJ8te0?start=39&autoplay=1&mute=1&loop=1&controls=0&showinfo=0&modestbranding=1&playlist=D5Yy2XJ8te0"
            title="Mystic Media Film reel"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 0,
              pointerEvents: 'none'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)'
            }}
          />
        </Box>

        {/* About block */}
        <Box
          ref={aboutRef}
          sx={{
            mt: { xs: 6, md: 8 },
            position: 'relative',
            borderRadius: 4,
            overflow: 'hidden',
            background: '#0a0a0b',
            color: '#ffffff',
            px: { xs: 4, md: 7 },
            py: { xs: 5, md: 7 },
            opacity: aboutVisible ? 1 : 0,
            transform: aboutVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.98)',
            filter: aboutVisible ? 'blur(0)' : 'blur(6px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1), filter 0.7s ease',
            willChange: 'opacity, transform, filter'
          }}
        >
          {/* Soft blue glow */}
          <Box
            sx={{
              position: 'absolute',
              top: '-30%',
              right: '-10%',
              width: 480,
              height: 480,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(37, 99, 235, 0.35) 0%, transparent 65%)',
              filter: 'blur(60px)',
              pointerEvents: 'none'
            }}
          />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ position: 'relative', zIndex: 1, mb: { xs: 4, md: 5 } }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.5)' }} />
              <Typography
                sx={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.8)',
                  letterSpacing: 1.4,
                  textTransform: 'uppercase'
                }}
              >
                About — 2026
              </Typography>
            </Stack>
            <Box
              component="img"
              src="/assets/logo-main.png"
              alt="Mystic Media Film"
              sx={{
                height: { xs: 24, md: 28 },
                width: 'auto',
                opacity: 0.9
              }}
            />
          </Stack>

          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: { xs: 24, md: 38 },
              fontWeight: 500,
              lineHeight: 1.2,
              letterSpacing: '-0.025em',
              color: '#ffffff',
              maxWidth: 880,
              position: 'relative',
              zIndex: 1
            }}
          >
            A small studio building bold work for the internet.{' '}
            <Box component="span" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              We move fast, ship sharp, and make things people actually remember.
            </Box>
          </Typography>

          {/* Services strip */}
          <Box
            sx={{
              mt: { xs: 5, md: 7 },
              pt: { xs: 3, md: 4 },
              borderTop: '1px solid rgba(255, 255, 255, 0.12)',
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
              gap: { xs: 2, md: 0 },
              position: 'relative',
              zIndex: 1
            }}
          >
            {['Branding', 'Video', 'Websites', 'Marketing', 'SEO', 'Ads'].map((label, i) => (
              <Stack
                key={label}
                direction="row"
                alignItems="baseline"
                spacing={1}
                sx={{
                  pl: { md: i === 0 ? 0 : 2 },
                  borderLeft: { md: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.1)' }
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Roboto Mono", monospace',
                    fontSize: 10,
                    fontWeight: 400,
                    color: 'rgba(255,255,255,0.4)',
                    letterSpacing: 0.5
                  }}
                >
                  0{i + 1}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#ffffff',
                    letterSpacing: '-0.01em'
                  }}
                >
                  {label}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
